import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User, Camera, MapPin, DollarSign, Briefcase, Star, Code,
  Save, ShieldCheck, ExternalLink, Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileEditPage = () => {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    full_name: '',
    bio: '',
    skills: '',
    location: '',
    portfolio_url: '',
    hourly_rate: ''
  });

  useEffect(() => {
    fetchProfile();
    if (user?.role === 'freelancer') {
      fetchBadges();
    }
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setProfile(res.data);
      setForm({
        full_name: res.data.full_name || '',
        bio: res.data.bio || '',
        skills: Array.isArray(res.data.skills) ? res.data.skills.join(', ') : (res.data.skills || ''),
        location: res.data.location || '',
        portfolio_url: res.data.portfolio_url || '',
        hourly_rate: res.data.hourly_rate || ''
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBadges = async () => {
    try {
      const [available, mine] = await Promise.all([
        axios.get(`${API}/badges`),
        axios.get(`${API}/badges/my`)
      ]);
      setAvailableBadges(available.data);
      setMyBadges(mine.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (form.full_name) params.append('full_name', form.full_name);
      if (form.bio !== undefined) params.append('bio', form.bio);
      if (form.skills !== undefined) params.append('skills', form.skills);
      if (form.location) params.append('location', form.location);
      if (form.portfolio_url) params.append('portfolio_url', form.portfolio_url);
      if (form.hourly_rate) params.append('hourly_rate', form.hourly_rate);

      const res = await axios.patch(`${API}/users/profile?${params.toString()}`);
      setProfile(res.data);
      if (setUser) setUser(res.data);
      toast.success('Profile updated successfully!');
    } catch (e) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.patch(`${API}/users/profile?avatar_url=${encodeURIComponent(uploadRes.data.url)}`);
      setProfile(prev => ({ ...prev, avatar_url: uploadRes.data.url }));
      toast.success('Avatar updated!');
    } catch (e) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handlePurchaseBadge = async (badgeId) => {
    try {
      await axios.post(`${API}/badges/${badgeId}/purchase`);
      toast.success('Badge purchased!');
      fetchBadges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to purchase badge');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">Loading profile...</div>
      </div>
    );
  }

  const avatarSrc = profile?.avatar_url
    ? (profile.avatar_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${profile.avatar_url}` : profile.avatar_url)
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-8" data-testid="profile-edit-title">
          Edit Profile
        </h1>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
            {user?.role === 'freelancer' && (
              <TabsTrigger value="badges" data-testid="tab-badges">Badges & Verification</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Avatar Card */}
              <Card className="md:col-span-1">
                <CardContent className="pt-6 text-center">
                  <div className="relative inline-block mb-4">
                    <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden mx-auto border-4 border-white shadow-lg">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-12 h-12 text-primary/40" />
                      )}
                    </div>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-secondary rounded-full flex items-center justify-center cursor-pointer shadow-md hover:bg-secondary/90 transition-colors">
                      <Camera className="w-4 h-4 text-white" />
                      <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="avatar-upload" />
                    </label>
                  </div>
                  {uploading && <p className="text-xs text-muted-foreground">Uploading...</p>}
                  <p className="font-heading font-bold text-lg">{profile?.full_name}</p>
                  <p className="text-sm text-muted-foreground">{profile?.email}</p>
                  <Badge className="mt-2 capitalize">{profile?.role}</Badge>

                  {myBadges.length > 0 && (
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {myBadges.map(b => (
                        <Badge key={b.id} style={{ backgroundColor: b.badge_color, color: '#fff' }}>
                          <ShieldCheck className="w-3 h-3 mr-1" /> {b.badge_name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Edit Form */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>Update your profile details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4" data-testid="profile-edit-form">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      data-testid="input-fullname"
                    />
                  </div>
                  <div>
                    <Label>Bio</Label>
                    <Textarea
                      value={form.bio}
                      onChange={(e) => setForm({ ...form, bio: e.target.value })}
                      rows={3}
                      placeholder="Tell clients about yourself..."
                      data-testid="input-bio"
                    />
                  </div>
                  <div>
                    <Label>Skills (comma separated)</Label>
                    <Input
                      value={form.skills}
                      onChange={(e) => setForm({ ...form, skills: e.target.value })}
                      placeholder="React, Node.js, Python, UI/UX"
                      data-testid="input-skills"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</Label>
                      <Input
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        placeholder="Mumbai, India"
                        data-testid="input-location"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> Hourly Rate (₹)</Label>
                      <Input
                        type="number"
                        value={form.hourly_rate}
                        onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                        placeholder="2500"
                        data-testid="input-hourly-rate"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Portfolio URL</Label>
                    <Input
                      value={form.portfolio_url}
                      onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })}
                      placeholder="https://myportfolio.com"
                      data-testid="input-portfolio-url"
                    />
                  </div>
                  <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-profile-button">
                    <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {user?.role === 'freelancer' && (
            <TabsContent value="badges">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Award className="w-5 h-5" /> Skill Verification Badges</CardTitle>
                  <CardDescription>Purchase badges to showcase your verified skills and build trust with clients</CardDescription>
                </CardHeader>
                <CardContent>
                  {availableBadges.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No badges available yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availableBadges.map(badge => {
                        const owned = myBadges.find(b => b.badge_id === badge.id);
                        return (
                          <Card key={badge.id} className={`border-2 transition-all ${owned ? 'border-green-300 bg-green-50/50' : 'hover:border-secondary/50'}`} data-testid={`badge-card-${badge.id}`}>
                            <CardContent className="pt-4 flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: badge.color + '20' }}>
                                <ShieldCheck className="w-6 h-6" style={{ color: badge.color }} />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-heading font-bold">{badge.name}</h3>
                                <p className="text-sm text-muted-foreground">{badge.description}</p>
                                <div className="flex items-center gap-3 mt-2">
                                  <span className="font-heading font-bold text-lg">₹{badge.price.toLocaleString('en-IN')}</span>
                                  <span className="text-xs text-muted-foreground">/ {badge.duration_days} days</span>
                                </div>
                                {owned ? (
                                  <Badge className="mt-2 bg-green-500">Active</Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    className="mt-2"
                                    onClick={() => handlePurchaseBadge(badge.id)}
                                    data-testid={`purchase-badge-${badge.id}`}
                                  >
                                    Purchase Badge
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default ProfileEditPage;
